import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table';
import { TableHeader } from '@tiptap/extension-table';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import { useCallback, useEffect } from 'react';
import TurndownService from 'turndown';
import MarkdownIt from 'markdown-it';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  Table as TableIcon,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Code,
  Undo,
  Redo,
  RemoveFormatting,
  Palette,
  Highlighter,
  Minus
} from 'lucide-react';

// Initialize markdown parser and converter
const md = new MarkdownIt({
  html: true,
  breaks: true,
  linkify: true
});

const turndownService = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  bulletListMarker: '-',
  br: '\n'  // Convert <br> to single newline
});

// Add rule for <br> tags to convert to newline
turndownService.addRule('lineBreak', {
  filter: 'br',
  replacement: function () {
    return '\n';
  }
});

// Add table support to turndown
turndownService.addRule('tableCell', {
  filter: ['th', 'td'],
  replacement: function (content, node) {
    return ' ' + content.trim() + ' |';
  }
});

turndownService.addRule('tableRow', {
  filter: 'tr',
  replacement: function (content, node) {
    return '|' + content + '\n';
  }
});

turndownService.addRule('table', {
  filter: 'table',
  replacement: function (content, node) {
    // Add header separator after first row
    const rows = content.trim().split('\n');
    if (rows.length > 0) {
      const headerCells = rows[0].split('|').filter(c => c.trim());
      const separator = '|' + headerCells.map(() => ' --- ').join('|') + '|';
      rows.splice(1, 0, separator);
    }
    return '\n' + rows.join('\n') + '\n';
  }
});

// Convert HTML to Markdown
const htmlToMarkdown = (html) => {
  if (!html) return '';
  return turndownService.turndown(html);
};

// Convert Markdown to HTML
const markdownToHtml = (markdown) => {
  if (!markdown) return '';
  return md.render(markdown);
};

const MenuBar = ({ editor }) => {
  const addImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl);
    
    if (url === null) return;
    
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const addTable = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  const buttonClass = (isActive) => 
    `p-1.5 rounded hover:bg-slate-200 transition-colors ${isActive ? 'bg-slate-200 text-blue-600' : 'text-slate-600'}`;

  if (!editor) return null;

  return (
    <div className="flex flex-wrap items-center gap-0.5 p-2 border-b bg-slate-50 rounded-t-md">
      {/* Text formatting */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={buttonClass(editor.isActive('bold'))}
        title="Bold"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={buttonClass(editor.isActive('italic'))}
        title="Italic"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={buttonClass(editor.isActive('underline'))}
        title="Underline"
      >
        <UnderlineIcon className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={buttonClass(editor.isActive('strike'))}
        title="Strikethrough"
      >
        <Strikethrough className="h-4 w-4" />
      </Button>

      {/* Text Color */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={buttonClass(editor.isActive('textStyle'))}
            title="Text Color"
          >
            <Palette className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align="start">
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-slate-500 mb-1">Text Color</span>
            <div className="grid grid-cols-8 gap-1">
              {[
                '#000000', '#374151', '#6b7280', '#9ca3af',
                '#ef4444', '#f97316', '#eab308', '#22c55e',
                '#14b8a6', '#3b82f6', '#6366f1', '#a855f7',
                '#ec4899', '#f43f5e', '#0ea5e9', '#8b5cf6',
              ].map((color) => (
                <button
                  key={color}
                  onClick={() => editor.chain().focus().setColor(color).run()}
                  className="w-6 h-6 rounded border border-slate-200 hover:scale-110 transition-transform"
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs mt-1"
              onClick={() => editor.chain().focus().unsetColor().run()}
            >
              Remove color
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Highlight */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={buttonClass(editor.isActive('highlight'))}
            title="Highlight"
          >
            <Highlighter className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align="start">
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-slate-500 mb-1">Highlight Color</span>
            <div className="grid grid-cols-6 gap-1">
              {[
                '#fef08a', '#fde047', '#fbbf24', // Yellows
                '#bbf7d0', '#86efac', '#4ade80', // Greens
                '#bfdbfe', '#93c5fd', '#60a5fa', // Blues
                '#fbcfe8', '#f9a8d4', '#f472b6', // Pinks
                '#fed7aa', '#fdba74', '#fb923c', // Oranges
                '#e9d5ff', '#d8b4fe', '#c084fc', // Purples
              ].map((color) => (
                <button
                  key={color}
                  onClick={() => editor.chain().focus().toggleHighlight({ color }).run()}
                  className="w-6 h-6 rounded border border-slate-200 hover:scale-110 transition-transform"
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs mt-1"
              onClick={() => editor.chain().focus().unsetHighlight().run()}
            >
              Remove highlight
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <div className="w-px h-6 bg-slate-300 mx-1" />

      {/* Headings */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={buttonClass(editor.isActive('heading', { level: 1 }))}
        title="Heading 1"
      >
        <Heading1 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={buttonClass(editor.isActive('heading', { level: 2 }))}
        title="Heading 2"
      >
        <Heading2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={buttonClass(editor.isActive('heading', { level: 3 }))}
        title="Heading 3"
      >
        <Heading3 className="h-4 w-4" />
      </Button>

      <div className="w-px h-6 bg-slate-300 mx-1" />

      {/* Lists */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={buttonClass(editor.isActive('bulletList'))}
        title="Bullet List"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={buttonClass(editor.isActive('orderedList'))}
        title="Numbered List"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>

      <div className="w-px h-6 bg-slate-300 mx-1" />

      {/* Block elements */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={buttonClass(editor.isActive('blockquote'))}
        title="Quote"
      >
        <Quote className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={buttonClass(editor.isActive('codeBlock'))}
        title="Code Block"
      >
        <Code className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        className={buttonClass(false)}
        title="Horizontal Rule"
      >
        <Minus className="h-4 w-4" />
      </Button>

      <div className="w-px h-6 bg-slate-300 mx-1" />

      {/* Links & Media */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={setLink}
        className={buttonClass(editor.isActive('link'))}
        title="Add Link"
      >
        <LinkIcon className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={addImage}
        className={buttonClass(false)}
        title="Add Image"
      >
        <ImageIcon className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={addTable}
        className={buttonClass(editor.isActive('table'))}
        title="Insert Table"
      >
        <TableIcon className="h-4 w-4" />
      </Button>

      <div className="w-px h-6 bg-slate-300 mx-1" />

      {/* Undo/Redo */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        className={buttonClass(false)}
        title="Undo"
      >
        <Undo className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        className={buttonClass(false)}
        title="Redo"
      >
        <Redo className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
        className={buttonClass(false)}
        title="Clear Formatting"
      >
        <RemoveFormatting className="h-4 w-4" />
      </Button>
    </div>
  );
};

/**
 * RichTextEditor Component
 * 
 * Props:
 * - value: Markdown string
 * - onChange: (markdown: string) => void
 * - placeholder: string
 * - minHeight: string (e.g., '200px')
 * - maxHeight: string (e.g., '500px')
 * - disabled: boolean
 * - className: string
 */
const RichTextEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Start writing...',
  minHeight = '200px',
  maxHeight = '500px',
  disabled = false,
  className = ''
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        },
        // Disable default list extensions so we can customize them
        bulletList: false,
        orderedList: false,
        listItem: false,
        // Disable extensions we're adding separately to avoid duplicates
        strike: true,  // Keep strike from StarterKit
      }),
      // Custom BulletList with explicit styling
      BulletList.configure({
        HTMLAttributes: {
          class: 'tiptap-bullet-list',
          style: 'list-style-type: disc; padding-left: 1.5rem; margin: 0.5rem 0;',
        },
      }),
      // Custom OrderedList with explicit styling
      OrderedList.configure({
        HTMLAttributes: {
          class: 'tiptap-ordered-list',
          style: 'list-style-type: decimal; padding-left: 1.5rem; margin: 0.5rem 0;',
        },
      }),
      // ListItem extension
      ListItem.configure({
        HTMLAttributes: {
          style: 'display: list-item;',
        },
      }),
      // Text styling extensions
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline hover:text-blue-800'
        }
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full rounded-lg'
        }
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse border border-slate-300'
        }
      }),
      TableRow,
      TableHeader.configure({
        HTMLAttributes: {
          class: 'bg-slate-100 border border-slate-300 p-2 font-semibold'
        }
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'border border-slate-300 p-2'
        }
      }),
      Placeholder.configure({
        placeholder
      })
    ],
    content: markdownToHtml(value),
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const markdown = htmlToMarkdown(html);
      onChange?.(markdown);
    }
  });

  // Update content when value changes externally
  useEffect(() => {
    if (editor && value !== undefined) {
      const currentMarkdown = htmlToMarkdown(editor.getHTML());
      if (currentMarkdown !== value) {
        // iter306x — pass `emitUpdate = false` so this programmatic
        // setContent doesn't fire onUpdate. Without this, the unavoidable
        // markdown→HTML→markdown round-trip (TipTap renders HTML; we
        // convert back to markdown for storage) re-emits the value as
        // a "user edit", which (1) made the parent's "Unsaved changes"
        // pill flicker back on immediately after a save, and (2) caused
        // the admin's Save endpoint to detect a content diff and bump
        // the document version — silently forcing every parent to
        // re-acknowledge a document that was effectively unchanged.
        editor.commands.setContent(markdownToHtml(value), false);
      }
    }
  }, [value, editor]);

  // Update editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  return (
    <div className={`border rounded-md bg-white ${disabled ? 'opacity-60' : ''} ${className}`}>
      <MenuBar editor={editor} />
      <EditorContent 
        editor={editor} 
        className="max-w-none focus:outline-none"
        style={{ 
          minHeight, 
          maxHeight, 
          overflowY: 'auto' 
        }}
      />
      <style>{`
        .ProseMirror {
          min-height: ${minHeight};
          outline: none;
          padding: 1rem 1.25rem;
        }
        .ProseMirror > * + * {
          margin-top: 0.75em;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #9ca3af;
          pointer-events: none;
          height: 0;
        }
        .ProseMirror p {
          margin: 0.5rem 0;
          line-height: 1.6;
        }
        .ProseMirror table {
          border-collapse: collapse;
          width: 100%;
          margin: 1rem 0;
        }
        .ProseMirror th,
        .ProseMirror td {
          border: 1px solid #e2e8f0;
          padding: 0.5rem;
          text-align: left;
        }
        .ProseMirror th {
          background-color: #f8fafc;
          font-weight: 600;
        }
        .ProseMirror blockquote {
          border-left: 4px solid #3b82f6 !important;
          padding: 0.75rem 1rem !important;
          margin: 1rem 0 !important;
          color: #64748b !important;
          font-style: italic;
          background-color: #f8fafc;
          border-radius: 0 0.25rem 0.25rem 0;
        }
        .ProseMirror pre {
          background-color: #1e293b;
          color: #e2e8f0;
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 1rem 0;
        }
        .ProseMirror code {
          background-color: #f1f5f9;
          padding: 0.125rem 0.375rem;
          border-radius: 0.25rem;
          font-size: 0.875rem;
          font-family: 'Monaco', 'Menlo', monospace;
        }
        .ProseMirror pre code {
          background: none;
          padding: 0;
        }
        .ProseMirror img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
        }
        .ProseMirror hr {
          border: none;
          border-top: 2px solid #e2e8f0;
          margin: 1.5rem 0;
        }
        /* Bullet Lists */
        .ProseMirror .tiptap-bullet-list,
        .ProseMirror ul {
          list-style-type: disc !important;
          padding-left: 1.5rem !important;
          margin: 0.75rem 0 !important;
        }
        .ProseMirror .tiptap-bullet-list > li,
        .ProseMirror ul > li {
          display: list-item !important;
          padding-left: 0.25rem !important;
          margin: 0.35rem 0 !important;
        }
        .ProseMirror .tiptap-bullet-list > li > p,
        .ProseMirror ul > li > p {
          margin: 0 !important;
        }
        /* Nested bullet lists */
        .ProseMirror .tiptap-bullet-list .tiptap-bullet-list,
        .ProseMirror ul ul {
          list-style-type: circle !important;
          margin: 0.25rem 0 !important;
        }
        .ProseMirror .tiptap-bullet-list .tiptap-bullet-list .tiptap-bullet-list,
        .ProseMirror ul ul ul {
          list-style-type: square !important;
        }
        /* Numbered Lists */
        .ProseMirror .tiptap-ordered-list,
        .ProseMirror ol {
          list-style-type: decimal !important;
          padding-left: 1.5rem !important;
          margin: 0.75rem 0 !important;
        }
        .ProseMirror .tiptap-ordered-list > li,
        .ProseMirror ol > li {
          display: list-item !important;
          padding-left: 0.25rem !important;
          margin: 0.35rem 0 !important;
        }
        .ProseMirror .tiptap-ordered-list > li > p,
        .ProseMirror ol > li > p {
          margin: 0 !important;
        }
        /* Headings with clear visual hierarchy */
        .ProseMirror h1,
        div[class*="ProseMirror"] h1 {
          font-size: 1.875rem !important;
          font-weight: 700 !important;
          color: #0f172a !important;
          margin-top: 1.5rem !important;
          margin-bottom: 0.75rem !important;
          line-height: 1.2 !important;
          border-bottom: 2px solid #3b82f6 !important;
          padding-bottom: 0.5rem !important;
          display: block !important;
        }
        .ProseMirror h2,
        div[class*="ProseMirror"] h2 {
          font-size: 1.5rem !important;
          font-weight: 600 !important;
          color: #1e40af !important;
          margin-top: 1.25rem !important;
          margin-bottom: 0.5rem !important;
          line-height: 1.3 !important;
          display: block !important;
        }
        .ProseMirror h3,
        div[class*="ProseMirror"] h3 {
          font-size: 1.25rem !important;
          font-weight: 600 !important;
          color: #475569 !important;
          margin-top: 1rem !important;
          margin-bottom: 0.5rem !important;
          line-height: 1.4 !important;
          font-style: italic !important;
          display: block !important;
        }
        /* First element shouldn't have top margin */
        .ProseMirror > *:first-child {
          margin-top: 0 !important;
        }
      `}</style>
    </div>
  );
};

// Export utilities for use elsewhere
export { htmlToMarkdown, markdownToHtml };
export default RichTextEditor;
